import React, { useState } from 'react';
import './Results.css';
import { generateReport } from '../utils/reportGenerator';
import Paywall from './Paywall';

function Results({ results, inputs, onScrollToTop }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [isCostBreakdownUnlocked, setIsCostBreakdownUnlocked] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Check for payment success in URL (after Stripe Checkout redirect)
  // This handles the case when user is redirected back from Stripe
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    
    if (success === 'true' && sessionId) {
      // Payment was successful - unlock the cost breakdown
      setIsCostBreakdownUnlocked(true);
      setShowPaywall(false);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Suppress Stripe-related errors that occur after redirect
  // These errors happen because Stripe's checkout script tries to initialize
  // but we're using hosted Checkout, so we don't need Stripe.js
  React.useEffect(() => {
    const handleError = (event) => {
      // Suppress Stripe CheckoutInitError and module loading errors
      if (event.error && (
        event.error.message?.includes('apiKey is not set') ||
        event.error.message?.includes('CheckoutInitError') ||
        event.error.message?.includes("Cannot find module './en'")
      )) {
        event.preventDefault();
        console.log('Suppressed Stripe initialization error (expected when using hosted Checkout)');
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Suppress unhandled promise rejections from Stripe
      if (event.reason && (
        event.reason.message?.includes('apiKey is not set') ||
        event.reason.message?.includes('CheckoutInitError') ||
        event.reason.message?.includes("Cannot find module './en'")
      )) {
        event.preventDefault();
        console.log('Suppressed Stripe promise rejection (expected when using hosted Checkout)');
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  // Check API key status on component mount
  React.useEffect(() => {
    const apiKey = process.env.REACT_APP_DEEPSEEK_API_KEY;
    if (apiKey) {
      console.log('✓ DeepSeek API key found in environment');
      console.log('Key length:', apiKey.length);
    } else {
      console.warn('⚠ DeepSeek API key not found. Set REACT_APP_DEEPSEEK_API_KEY in .env file and restart dev server.');
    }
  }, []);
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const roiPercentage = Math.min(results.totals.roiPct, 300);
  const roiBarWidth = Math.min((roiPercentage / 300) * 100, 100);

  return (
    <div className="results">
      <h2 className="section-title">Results</h2>

      <div className="results-cards-top">
        <div className="result-card primary">
          <div className="card-label">Total Annual Cost of Instability</div>
          <div className="card-value">{formatCurrency(results.totals.totalInstabilityCost)}</div>
        </div>

        <div className="result-card success">
          <div className="card-label">Projected Annual Savings</div>
          <div className="card-value">{formatCurrency(results.totals.projectedSavings)}</div>
          <div className="card-subtitle">
            Uplift {inputs.retentionUplift}% • TTF ↓ {inputs.timeToFillReduction}%
          </div>
        </div>
      </div>

      <div className="results-cards-middle">
        <div className="result-card info">
          <div className="card-label">International Recruitment Savings (subset)</div>
          <div className="card-value">{formatCurrency(results.subsets.intlRecruitmentSavingsSubset)}</div>
        </div>

        <div className="result-card roi">
          <div className="card-label">ROI of Stabilized Workforce</div>
          <div className="card-value">{results.totals.roiPct.toFixed(1)}%</div>
          <div className="roi-bar-container">
            <div className="roi-bar" style={{ width: `${roiBarWidth}%` }}></div>
          </div>
          <div className="card-subtitle">
            Savings ÷ Program cost • Bar caps at 300% for display
          </div>
        </div>
      </div>

      <div className={`cost-breakdown-wrapper ${!isCostBreakdownUnlocked ? 'cost-breakdown-locked' : ''}`}>
        <div className="cost-breakdown-content">
          <div className="cost-breakdown">
            <h3 className="breakdown-title">Cost breakdown</h3>
            <div className="breakdown-grid">
              <div className="breakdown-item">
                <span className="breakdown-label">Turnover cost</span>
                <span className="breakdown-value">
                  {formatCurrency(results.breakdown.turnoverCost)}
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Productivity loss</span>
                <span className="breakdown-value">
                  {formatCurrency(results.breakdown.productivityLoss)}
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Absenteeism</span>
                <span className="breakdown-value">
                  {formatCurrency(results.breakdown.absenteeismCost)}
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Training & ramp</span>
                <span className="breakdown-value">
                  {formatCurrency(results.breakdown.trainingRampCost)}
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Brand penalty</span>
                <span className="breakdown-value">
                  {formatCurrency(results.breakdown.brandPenalty)}
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Opportunity loss</span>
                <span className="breakdown-value">
                  {formatCurrency(results.breakdown.opportunityLoss)}
                </span>
              </div>
            </div>
          </div>

          <div className="summary">
            <p>
              By improving retention by {inputs.retentionUplift}% and reducing time-to-fill by {inputs.timeToFillReduction}%, 
              this demo estimates {formatCurrency(results.totals.projectedSavings)} in annual savings. 
              Based on a program cost of {formatCurrency(inputs.programCost)}, modeled ROI is {results.totals.roiPct.toFixed(1)}%. 
              Assumptions are illustrative only and should be tailored per industry.
            </p>
            <div className="meta-info">
              <details>
                <summary>View calculation assumptions</summary>
                <div className="assumptions">
                  <p><strong>Version:</strong> {results.meta.version}</p>
                  <ul>
                    <li>Productivity factor (vacancy): {(results.meta.assumptions.productivityFactorVacancy * 100).toFixed(0)}%</li>
                    <li>Productivity factor (absenteeism): {(results.meta.assumptions.productivityFactorAbsent * 100).toFixed(0)}%</li>
                    <li>Training & ramp: {(results.meta.assumptions.trainingRampPctSalary * 100).toFixed(0)}% of salary</li>
                    <li>Brand penalty multiplier: {(results.meta.assumptions.brandPenaltyMultiplier * 100).toFixed(0)}%</li>
                    <li>Revenue unrealized: {(results.meta.assumptions.revenueUnrealizedPct * 100).toFixed(0)}%</li>
                    <li>Absenteeism improvement: {(results.meta.assumptions.absenteeismImprovementPct * 100).toFixed(0)}%</li>
                  </ul>
                </div>
              </details>
            </div>
          </div>
        </div>
        {!isCostBreakdownUnlocked && showPaywall && (
          <Paywall
            onUnlock={() => {
              setIsCostBreakdownUnlocked(true);
              setShowPaywall(false);
            }}
            onClose={() => setShowPaywall(false)}
          />
        )}
        {!isCostBreakdownUnlocked && !showPaywall && (
          <div className="cost-breakdown-lock-overlay">
            <div className="lock-overlay-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1C8.13 1 5 4.13 5 8V10H4C2.9 10 2 10.9 2 12V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V12C22 10.9 21.1 10 20 10H19V8C19 4.13 15.87 1 12 1ZM12 3C14.76 3 17 5.24 17 8V10H7V8C7 5.24 9.24 3 12 3Z" fill="currentColor"/>
              </svg>
              <p>Unlock to view full cost breakdown</p>
              <button className="unlock-btn" onClick={() => setShowPaywall(true)}>Unlock Now</button>
            </div>
          </div>
        )}
      </div>

      <div className="cta-section">
        <p className="cta-text">
          Would like to have a detailed report and understand how you can improve your organization efficiency?{' '}
          <a href="#" className="cta-link" onClick={(e) => {
            e.preventDefault();
            // TODO: Replace with actual booking link
            window.open('https://calendar.app.google/uP134WqSSECYok8n6', '_blank');
          }}>
            Click here to book a meeting with our expert
          </a>
        </p>
      </div>

      {/* <div className="results-buttons">
        <button 
          className="btn btn-blue" 
          onClick={async () => {
            setIsGenerating(true);
            setError(null);
            try {
              console.log('Starting report generation...');
              await generateReport(inputs, results);
              console.log('Report generation completed');
            } catch (err) {
              const errorMessage = err.message || 'Failed to generate report';
              setError(errorMessage);
              console.error('Report generation error:', err);
              
              // Show user-friendly error message
              if (errorMessage.includes('401') || errorMessage.includes('Authentication')) {
                alert(
                  'API Authentication Failed\n\n' +
                  'Your DeepSeek API key appears to be invalid.\n\n' +
                  'Please check:\n' +
                  '1. Your API key is correct in the .env file\n' +
                  '2. There are no extra spaces around the key\n' +
                  '3. Your account has sufficient balance\n' +
                  '4. The API key is active\n\n' +
                  'Get your API key from: https://platform.deepseek.com/\n\n' +
                  'Note: The report will still be generated without AI analysis.'
                );
              } else {
                alert(`Error: ${errorMessage}\n\nCheck the browser console for more details.`);
              }
            } finally {
              setIsGenerating(false);
            }
          }}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating Report...' : 'Generate Report'}
        </button>
        <button className="btn btn-gray" onClick={onScrollToTop}>
          Back to top
        </button>
      </div> */}
      {error && (
        <div style={{ 
          marginTop: '10px', 
          padding: '10px', 
          backgroundColor: '#fee', 
          color: '#c33', 
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          Error: {error}
        </div>
      )}
    </div>
  );
}

export default Results;

